import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';

import {
  AzTableComponent,
  AzTableColumn,
  AzTableAction,
} from '../../../../shared/components/az-table/az-table.component';
import { AzButtonComponent } from '../../../../shared/components/az-button/az-button.component';
import { AzDocumentUpdateModalComponent } from '../../../../shared/components/az-document-update-modal/az-document-update-modal.component';
import { StatusChangeModalComponent } from '../../../../shared/components/status-change-modal/status-change-modal.component';
import { LoggerService } from '../../../../core/logging/logger.service';
import { LegalEntityService, DocumentFile } from '../../services/legal-entity.service';
import { UserPermissionsService } from '../../../../core/auth/services/user-permissions.service';
import { SpinnerComponent } from '../../../../shared/components/spinner/spinner.component';
import { HttpEventType } from '@angular/common/http';
import { switchMap } from 'rxjs/operators';
import { Subscription, forkJoin } from 'rxjs';
import { SelectedLegalEntityService } from '../../../../shared/services/selected-legal-entity.service';

interface DocumentRow {
  document_file_id: number;
  document: string;
  version: number;
  uploadedBy: string;
  uploadDate: string;
  comments: string;
}

@Component({
  selector: 'app-legal-entity-documents',
  imports: [
    AzTableComponent,
    AzButtonComponent,
    AzDocumentUpdateModalComponent,
    StatusChangeModalComponent,
    SpinnerComponent,
  ],
  templateUrl: './legal-entity-documents.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './legal-entity-documents.component.scss',
})
export class LegalEntityDocumentsComponent implements OnInit, OnChanges, OnDestroy {
  private statusChangeSubscription?: Subscription;
  private loadSubscription?: Subscription;
  private reloadSubscription?: Subscription;

  constructor(
    private logger: LoggerService,
    private legalEntityService: LegalEntityService,
    public userPermissions: UserPermissionsService,
    private selectedLegalEntity: SelectedLegalEntityService,
  ) {}

  @Input() entityId: number | null = null;

  @ViewChild(StatusChangeModalComponent) statusModal!: StatusChangeModalComponent;
  @ViewChild(AzDocumentUpdateModalComponent) documentUpdateModal!: AzDocumentUpdateModalComponent;

  documents: DocumentRow[] = [];
  rawDocuments: DocumentFile[] = []; // Store raw documents for LOA deletion logic
  loading: boolean = false;
  uploading: boolean = false;
  isModalOpen: boolean = false;
  isStatusModalOpen: boolean = false;
  documentTypes: string[] = []; // Will be loaded from API
  documentTypeObjects: Array<{ name: string; type: string }> = []; // Store full objects
  allowedFileTypes: string[] = []; // Unique file types from documentTypeObjects

  // DDQ template validation state
  showDdqConfirmModal: boolean = false;
  showProcessingModal: boolean = false;
  showDdqSuccessModal: boolean = false;
  pendingUploadData: { file: File | null; documentType: string; comments: string } | null = null;
  ddqNomineeName: string = '';
  isUploadingDdqTemplate: boolean = false;

  // LOA (Signed 2/2) confirmation state
  showLoaConfirmModal: boolean = false;

  columns: AzTableColumn[] = [
    {
      key: 'document',
      label: 'Document',
      width: '20.33%',
    },
    {
      key: 'version',
      label: 'Version',
      width: '11.14%',
    },
    {
      key: 'uploadedBy',
      label: 'Uploaded By',
      width: '16.43%',
    },
    {
      key: 'uploadDate',
      label: 'Upload Date',
      width: '16.87%',
    },
    {
      key: 'comments',
      label: 'Comments',
    },
  ];
  actions: AzTableAction<DocumentRow>[] = [
    {
      icon: 'download',
      label: 'Download',
      callback: (row: DocumentRow) => this.downloadDocument(row),
    },
  ];

  /**
   * Getter to check if user has permission to upload documents
   */
  get canUploadDocuments(): boolean {
    return this.userPermissions.hasUploadDocumentPermission();
  }

  ngOnInit(): void {
    this.loadDocuments();
    this.loadDocumentTypes();

    // Subscribe to status change approval events to reload documents
    this.statusChangeSubscription = this.selectedLegalEntity.statusChangeApproved$.subscribe(() => {
      this.logger.info('Status change approved, reloading documents');
      this.loadDocuments();
    });

    // Subscribe to entity detail reload events (e.g., when DDQ Nominee is saved)
    this.reloadSubscription = this.selectedLegalEntity.reloadEntityDetail$.subscribe(() => {
      this.logger.info('Entity detail reloaded, data will be refreshed on next action');
      // No need to do anything here - the entityDetail signal will be updated
      // and will be read fresh when handleUpload is called
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['entityId'] && !changes['entityId'].firstChange) {
      this.logger.info('Entity ID changed, reloading documents', {
        previousId: changes['entityId'].previousValue,
        currentId: changes['entityId'].currentValue,
      });
      this.loadSubscription?.unsubscribe();
      this.loadDocuments();
    }
  }

  ngOnDestroy(): void {
    this.statusChangeSubscription?.unsubscribe();
    this.loadSubscription?.unsubscribe();
    this.reloadSubscription?.unsubscribe();
  }

  loadDocuments(): void {
    if (!this.entityId) {
      return;
    }
    this.loading = true;
    // Load both documents and entity detail in parallel, wait for both to complete
    this.loadSubscription = forkJoin({
      documents: this.legalEntityService.getLegalEntityDocuments(this.entityId),
      detail: this.legalEntityService.getLegalEntityDetail(this.entityId),
    }).subscribe({
      next: ({ documents: documentFiles, detail }) => {
        this.rawDocuments = documentFiles; // Store raw documents
        this.documents = this.transformDocuments(documentFiles);
        // Update the entity detail in the shared service
        this.selectedLegalEntity.setEntityDetail(detail);
        this.selectedLegalEntity.setStatus(detail.bcr_status?.status_name || '');
        this.selectedLegalEntity.setBcrStatusId(detail.bcr_status?.id || null);
        this.loading = false;
      },
      error: (error) => {
        this.logger.error('Error loading documents:', error);
        this.documents = [];
        this.loading = false;

        // Get appropriate error message based on error type
        const errorInfo = this.getErrorMessage(
          error,
          'An error occurred while loading documents. Please try again.',
        );

        // Show error modal
        this.isStatusModalOpen = true;
        setTimeout(() => {
          if (this.statusModal) {
            this.statusModal.showError(errorInfo.message, errorInfo.title, 'error');
          }
        }, 0);
      },
    });
  }

  /**
   * Load available document types from backend
   */
  loadDocumentTypes(): void {
    this.legalEntityService.getDocumentTypes().subscribe({
      next: (types) => {
        // Store full objects
        this.documentTypeObjects = types;
        // Transform DocumentType[] to string[] for the selector
        this.documentTypes = types.map((type) => type.name);
        // Extract unique file types
        this.allowedFileTypes = [...new Set(types.map((type) => type.type))];
        this.logger.info(
          `Loaded ${this.documentTypes.length} document types with allowed file types:`,
          this.allowedFileTypes,
        );
      },
      error: (error) => {
        this.logger.error('Error loading document types:', error);
        // Fallback to empty arrays if API fails
        this.documentTypes = [];
        this.allowedFileTypes = [];
      },
    });
  }

  /**
   * Transform DocumentFile from API to DocumentRow for table display
   */
  private transformDocuments(documentFiles: DocumentFile[]): DocumentRow[] {
    return documentFiles.map((doc) => ({
      document_file_id: doc.document_file_id,
      document: doc.document_type,
      version: doc.version,
      uploadedBy: doc.uploaded_by || '--',
      uploadDate: this.formatDate(doc.created_date),
      comments: doc.comments || '--',
    }));
  }

  /**
   * Format Unix timestamp to readable date string (DD/MMM/YYYY)
   */
  private formatDate(timestamp: number): string {
    const date = new Date(timestamp * 1000);
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  downloadDocument(row: DocumentRow): void {
    if (!this.entityId) {
      this.logger.error('Cannot download: No entity ID');
      return;
    }

    this.logger.info(`Downloading document: ${row.document} (ID: ${row.document_file_id})`);

    this.legalEntityService.generateDownloadUrl(this.entityId, row.document_file_id).subscribe({
      next: (response) => {
        this.logger.info('Download URL generated successfully');
        // Use the downloadFile helper method to trigger browser download
        this.legalEntityService.downloadFile(response.download_url, response.filename);
      },
      error: (error) => {
        this.logger.error('Error generating download URL:', error);
      },
    });
  }

  onRowClick(_row: DocumentRow): void {
    // TODO: Implement row click behavior (e.g., open document preview)
    // Selected row: row
  }

  openModal(): void {
    this.isModalOpen = true;
  }

  closeModal(): void {
    this.isModalOpen = false;
    // Reset the modal form when closing
    if (this.documentUpdateModal) {
      this.documentUpdateModal.resetForm();
    }
  }

  closeStatusModal(): void {
    this.isStatusModalOpen = false;
  }

  closeDdqConfirmModal(): void {
    this.showDdqConfirmModal = false;
    this.pendingUploadData = null;
  }

  closeProcessingModal(): void {
    this.showProcessingModal = false;
  }

  closeDdqSuccessModal(): void {
    this.showDdqSuccessModal = false;
  }

  closeLoaConfirmModal(): void {
    this.showLoaConfirmModal = false;
    this.pendingUploadData = null;
  }

  onCancelLoaUpload(): void {
    this.logger.info('LOA (Signed 2/2) upload cancelled by user');
    this.closeLoaConfirmModal();
  }

  onConfirmLoaUpload(): void {
    this.logger.info('LOA (Signed 2/2) upload confirmed by user');

    if (this.pendingUploadData) {
      // Store the data before closing modal (which clears pendingUploadData)
      const uploadData = this.pendingUploadData;
      this.closeLoaConfirmModal();

      // Proceed with the actual upload (which will delete LOA Signed 1/2)
      this.performUpload(uploadData);
    } else {
      this.closeLoaConfirmModal();
    }
  }

  onCancelDdqUpload(): void {
    this.logger.info('DDQ template upload cancelled by user');
    this.closeDdqConfirmModal();
  }

  onConfirmDdqUpload(): void {
    this.logger.info('DDQ template upload confirmed by user');

    if (this.pendingUploadData) {
      // Mark this upload as DDQ template to show appropriate success modal
      this.isUploadingDdqTemplate = true;

      // Store the data before closing modal (which clears pendingUploadData)
      const uploadData = this.pendingUploadData;
      this.closeDdqConfirmModal();

      // Proceed with the actual upload
      this.performUpload(uploadData);
    } else {
      this.closeDdqConfirmModal();
    }
  }

  handleUpload(data: { file: File | null; documentType: string; comments: string }): void {
    if (!this.entityId) {
      this.logger.error('Cannot upload: No entity ID');
      return;
    }

    if (!data.file) {
      this.logger.error('Cannot upload: No file selected');
      return;
    }

    if (!data.documentType) {
      this.logger.error('Cannot upload: No document type selected');
      return;
    }

    // Check if DDQ template is being uploaded and bcr_status id is 7
    const entityDetail = this.selectedLegalEntity.entityDetail();
    const isDdqTemplate = data.documentType === 'DDQ template';
    const isPendingDdqNominee = entityDetail?.bcr_status?.id === 7;

    if (isDdqTemplate && isPendingDdqNominee) {
      // Validate that DDQ Nominee exists before showing confirmation
      const ddqNominee = entityDetail?.ddq_specific_fields?.ddq_nominee;

      if (!ddqNominee) {
        // DDQ Nominee is missing - show error modal
        this.closeModal();
        this.isStatusModalOpen = true;
        setTimeout(() => {
          if (this.statusModal) {
            this.statusModal.showError(
              'In order to replace the DDQ File please provide the DDQ Nominee and their email.',
            );
          }
        }, 0);
        return;
      }

      // DDQ Nominee exists - proceed with confirmation modal
      this.pendingUploadData = data;
      this.ddqNomineeName = ddqNominee;
      this.showDdqConfirmModal = true;
      this.closeModal(); // Close the upload modal
      return;
    }

    // Check if uploading LOA (Signed 2/2) and LOA (Signed 1/2) exists
    if (data.documentType === 'LOA (Signed 2/2)') {
      const existingLoaSigned1 = this.rawDocuments.find(
        (doc) => doc.document_type === 'LOA (Signed 1/2)',
      );

      if (existingLoaSigned1) {
        // Show confirmation modal before upload
        this.pendingUploadData = data;
        this.showLoaConfirmModal = true;
        this.closeModal(); // Close the upload modal
        return;
      }
    }

    this.logger.info('Starting document upload process...', {
      documentType: data.documentType,
      fileName: data.file.name,
      fileSize: data.file.size,
    });

    this.performUpload(data);
  }

  private performUpload(data: { file: File | null; documentType: string; comments: string }): void {
    // Show loading state and processing modal
    this.uploading = true;
    this.showProcessingModal = true;

    // Check if this is a LOA document type requiring special handling
    const loaTypes = ['LOA (Signed 1/2)', 'LOA (Signed 2/2)', 'LOI (Completed)'];
    const isLoaDocument = loaTypes.includes(data.documentType);

    // Check for mutual exclusion between LOA (Signed 1/2) and LOA (Signed 2/2)
    const needsMutualExclusion =
      data.documentType === 'LOA (Signed 1/2)' || data.documentType === 'LOA (Signed 2/2)';

    if (needsMutualExclusion) {
      // Determine which document type to delete
      const typeToDelete =
        data.documentType === 'LOA (Signed 1/2)' ? 'LOA (Signed 2/2)' : 'LOA (Signed 1/2)';

      // Find if the conflicting document exists
      const existingDoc = this.rawDocuments.find((doc) => doc.document_type === typeToDelete);

      if (existingDoc) {
        this.logger.info(
          `Deleting conflicting document ${typeToDelete} before uploading ${data.documentType}`,
          { document_file_id: existingDoc.document_file_id },
        );

        // Delete the existing document first, then proceed with upload
        this.legalEntityService
          .deleteDocument(this.entityId!, existingDoc.document_file_id)
          .subscribe({
            next: (deleteResponse) => {
              this.logger.info('Document deleted successfully', deleteResponse);
              // Now proceed with the upload
              this.proceedWithUpload(data, isLoaDocument);
            },
            error: (error) => {
              this.logger.error('Error deleting conflicting document:', error);
              this.uploading = false;
              this.showProcessingModal = false;

              const errorInfo = this.getErrorMessage(
                error,
                'Failed to delete existing document before upload. Please try again.',
              );

              this.isStatusModalOpen = true;
              setTimeout(() => {
                if (this.statusModal) {
                  this.statusModal.showError(errorInfo.message, errorInfo.title, 'error');
                }
              }, 0);
            },
          });
        return; // Exit early, will continue in the delete callback
      }
    }

    // No deletion needed, proceed directly with upload
    this.proceedWithUpload(data, isLoaDocument);
  }

  /**
   * Proceed with the actual upload after any necessary deletions
   */
  private proceedWithUpload(
    data: { file: File | null; documentType: string; comments: string },
    isLoaDocument: boolean,
  ): void {
    // Step 1: Generate presigned upload URL (use LOA endpoint if applicable)
    const uploadRequest = {
      document_type_name: data.documentType,
      comments: data.comments || undefined,
    };

    // Store document_file_id for step 3
    let documentFileId: number;

    // Choose the appropriate upload URL generation method
    const uploadUrl$ = isLoaDocument
      ? this.legalEntityService.generateLoaUploadUrl(this.entityId!, uploadRequest)
      : this.legalEntityService.generateUploadUrl(this.entityId!, uploadRequest);

    uploadUrl$
      .pipe(
        switchMap((uploadResponse) => {
          this.logger.info('Presigned URL generated successfully', {
            document_file_id: uploadResponse.document_file_id,
            s3_key: uploadResponse.s3_key,
            expires_in: uploadResponse.expires_in,
          });

          // Store document_file_id for confirmation step
          documentFileId = uploadResponse.document_file_id;

          // Step 2: Upload file to S3 using presigned URL
          return this.legalEntityService.uploadToS3(data.file!, uploadResponse.presigned_post);
        }),
        switchMap((event) => {
          // Track upload progress
          if (event.type === HttpEventType.UploadProgress) {
            const progress = event.total ? Math.round((100 * event.loaded) / event.total) : 0;
            this.logger.info(`Upload progress: ${progress}%`);
            // Return empty observable to continue chain
            return [];
          } else if (event.type === HttpEventType.Response) {
            this.logger.info('File uploaded to S3 successfully');
            // Step 3: Confirm upload to activate document record
            return this.legalEntityService.confirmUpload(documentFileId);
          }
          return [];
        }),
      )
      .subscribe({
        next: (confirmResponse) => {
          // Only log if we have a confirmation response (not progress events)
          if (confirmResponse) {
            this.logger.info('Upload confirmed successfully', confirmResponse);

            // Hide loading state and processing modal
            this.uploading = false;
            this.showProcessingModal = false;

            // Close upload modal
            this.closeModal();

            // Reload documents
            this.loadDocuments();

            // Show appropriate success modal
            if (this.isUploadingDdqTemplate) {
              // Show DDQ-specific success modal with warning
              this.showDdqSuccessModal = true;
              this.isUploadingDdqTemplate = false; // Reset flag
            } else {
              // Show regular success modal
              this.isStatusModalOpen = true;
              setTimeout(() => {
                if (this.statusModal) {
                  this.statusModal.showSuccessWithMessage(
                    'Document uploaded successfully!',
                    'Documents',
                  );
                }
              }, 0);
            }
          }
        },
        error: (error) => {
          this.logger.error('Error during upload process:', error);
          this.uploading = false;
          this.showProcessingModal = false;
          this.isUploadingDdqTemplate = false; // Reset flag

          // Check if it's a timeout error and show appropriate message
          const errorInfo = this.getErrorMessage(
            error,
            'An error occurred during the upload process. Please try again.',
          );

          // Show error modal
          this.isStatusModalOpen = true;
          setTimeout(() => {
            if (this.statusModal) {
              this.statusModal.showError(errorInfo.message, errorInfo.title, 'error');
            }
          }, 0);
        },
      });
  }

  /**
   * Check if error is a timeout error
   */
  private isTimeoutError(error: unknown): boolean {
    const err = error as {
      name?: string;
      message?: string;
      status?: number;
      statusText?: string;
      error?: { message?: string };
    };
    return (
      err?.name === 'TimeoutError' ||
      (err?.message?.toLowerCase().includes('timeout') ?? false) ||
      err?.status === 504 || // Gateway Timeout
      err?.status === 408 || // Request Timeout
      (err?.error?.message?.toLowerCase().includes('timeout') ?? false) ||
      // Browser timeout: status 0 with Unknown Error
      (err?.status === 0 && err?.statusText === 'Unknown Error')
    );
  }

  /**
   * Get appropriate error message based on error type
   */
  private getErrorMessage(
    error: unknown,
    defaultMessage: string,
  ): { title: string; message: string } {
    if (this.isTimeoutError(error)) {
      return {
        title: 'Request Timeout',
        message:
          'The file took too long to process. Please check your connectivity and/or file size and try again.\n\nIf this error keeps occurring please contact support.',
      };
    }

    // Extract backend message or use default
    const err = error as { error?: { message?: string }; message?: string };
    const message = err.error?.message || err.message || defaultMessage;

    // Infer a sensible title from the default message (operation context)
    const op = defaultMessage.toLowerCase();
    let title = 'Request Failed';

    if (op.includes('upload')) {
      title = 'Upload Failed';
    } else if (op.includes('download')) {
      title = 'Download Failed';
    } else if (op.includes('load')) {
      title = 'Load Failed';
    }

    return {
      title,
      message,
    };
  }
}
